import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { promotionsApi } from '../../services/promotionsApi';
import { PromoHolderRow, PromoHolderStatus } from '../../types/promotions';
import CursorPagination from '../CursorPagination';
import { MessageStatusChip } from './PromoStatusChip';
import { formatPromoDate, holderLabel, sourceLabel } from './promoFormat';

const FILTERS: { key: PromoHolderStatus | 'ALL'; label: string }[] = [
  { key: 'ALL', label: 'All' }, { key: 'ACTIVE', label: 'Active' }, { key: 'PENDING', label: 'Pending' }, { key: 'REVOKED', label: 'Revoked' },
];

export const REVOKE_BLOCKED = 'This customer has already used the code.';

interface HoldersTableProps {
  promoId: string;
  code: string;
  token: string;
  /** Bumped by the page after a share so the list refetches. */
  refreshKey: number;
  /** A revoke changes the slot count — the page refetches its summary. */
  onChanged: () => void;
  notify: (message: string, type?: 'success' | 'error') => void;
  onShare?: () => void;
}

/** Flow D — who holds the code, how they got it, delivery status per channel, uses; Revoke / Resend. */
export default function HoldersTable({ promoId, code, token, refreshKey, onChanged, notify, onShare }: HoldersTableProps) {
  const [filter, setFilter] = useState<PromoHolderStatus | 'ALL'>('ALL');
  const [rows, setRows] = useState<PromoHolderRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async (after: string | null) => {
    if (after) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const page = await promotionsApi.holders(promoId, { status: filter === 'ALL' ? undefined : filter, cursor: after ?? undefined, size: 20 }, token);
      setRows((prev) => (after ? [...prev, ...page.content] : page.content));
      setCursor(page.nextCursor ?? null);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load holders');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [promoId, token, filter]);

  useEffect(() => { load(null); }, [load, refreshKey]);

  const act = async (id: string, fn: () => Promise<unknown>, ok: string) => {
    setBusyId(id);
    try {
      await fn();
      notify(ok);
      await load(null);
      onChanged();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Action failed', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const revoke = (h: PromoHolderRow) => {
    if (!window.confirm(`Revoke ${code} from ${holderLabel(h)}? The code leaves their wallet and the slot is freed.`)) return;
    act(h.id, () => promotionsApi.revoke(promoId, h.id, token), 'Share revoked — slot freed');
  };

  return (
    <div className="admin-card" data-testid="holders-table">
      <div className="admin-card-header">
        <h3 className="admin-card-title">Holders</h3>
        <div role="tablist" aria-label="Holder status" style={{ display: 'flex', gap: '0.25rem' }}>
          {FILTERS.map((f) => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key} className={`promo-tab${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="admin-loading"><div className="admin-loading-spinner" /></div>
      ) : error ? (
        <div>
          <div className="admin-info-banner danger" role="alert">{error}</div>
          <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" onClick={() => load(null)}>Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="admin-empty-state" data-testid="holders-empty">
          {filter === 'ALL' ? (
            <p>No one holds this code yet — {onShare ? <button type="button" className="promo-inline-link" onClick={onShare}>Share it.</button> : 'Share it.'}</p>
          ) : <p>No holders in this view.</p>}
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr><th>Customer</th><th>Source</th><th>Delivery</th><th>Date</th><th style={{ textAlign: 'right' }}>Uses</th><th>Last order</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((h) => {
                const used = h.usesConsumed > 0;
                const failed = h.messages.filter((m) => m.status === 'FAILED');
                return (
                  <tr key={h.id} data-testid="holder-row">
                    <td>
                      <div style={{ fontWeight: 500 }}>{holderLabel(h)}</div>
                      {h.status === 'REVOKED' && <span className="admin-badge rejected">Revoked</span>}
                    </td>
                    <td>{sourceLabel(h)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {h.messages.length === 0 ? '—' : h.messages.map((m) => <MessageStatusChip key={m.id} channel={m.channel} status={m.status} error={m.error} />)}
                      </div>
                    </td>
                    <td>{formatPromoDate(h.claimedAt ?? h.sharedAt, true)}</td>
                    <td style={{ textAlign: 'right' }}>{h.usesConsumed}/{h.usesPerHolder}</td>
                    <td>{h.lastOrder ? <Link to={`/orders/${h.lastOrder.id}`} className="admin-link">{h.lastOrder.orderNumber ?? 'Order'}</Link> : '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {failed.map((m) => (
                        <button key={m.id} type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={busyId !== null}
                          onClick={() => act(h.id, () => promotionsApi.resend(promoId, m.id, token), 'Message re-sent')}>
                          Resend {m.channel === 'WHATSAPP' ? 'WhatsApp' : 'email'}
                        </button>
                      ))}
                      {h.status !== 'REVOKED' && (
                        <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={used || busyId !== null}
                          title={used ? REVOKE_BLOCKED : undefined} onClick={() => revoke(h)}>
                          {busyId === h.id ? 'Working…' : 'Revoke'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loading && !error && <CursorPagination hasMore={hasMore} loading={loadingMore} onLoadMore={() => load(cursor)} />}
    </div>
  );
}
